import { CornerDownRight } from "lucide-react-native";
import { FC } from "react";

import { HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Question, Rule } from "@/types/resources";
import { useOperatorLabels } from "./operators";

interface SkipRulesProps {
  allQuestions: Question[];
  rules: Rule[];
}

const SkipRules: FC<SkipRulesProps> = ({ allQuestions, rules }) => {
  const { i18n } = useLocalization();
  const operatorLabels = useOperatorLabels();

  if (rules.length === 0) return null;

  const conditionalRules = rules
    .filter((r) => r.source_question_id !== null && r.option_index !== null)
    .sort((a, b) => {
      const aSourceOrder =
        allQuestions.find((q) => q.id === a.source_question_id)?.order ?? 0;
      const bSourceOrder =
        allQuestions.find((q) => q.id === b.source_question_id)?.order ?? 0;
      if (aSourceOrder !== bSourceOrder) return aSourceOrder - bSourceOrder;
      return (a.option_index ?? 0) - (b.option_index ?? 0);
    });
  const defaultRules = rules.filter(
    (r) => r.option_index === null && r.source_question_id === null
  );
  const probabilityBasedRules = defaultRules.filter(
    (r) => r.probability !== null
  );
  const fallbackDefaultRule = defaultRules.find((r) => r.probability === null);

  if (
    conditionalRules.length === 0 &&
    probabilityBasedRules.length === 0 &&
    !fallbackDefaultRule
  )
    return null;

  const getTargetDisplay = (rule: Rule) => {
    if (rule.target_question_id === null) {
      return i18n.t("questions.endSurvey");
    }
    const targetOrder = allQuestions.find(
      (q) => q.id === rule.target_question_id
    )?.order;
    return i18n.t("questions.skipToQuestion", { number: targetOrder });
  };

  const getProbabilityRulesDisplay = () => {
    if (probabilityBasedRules.length === 0) return null;

    const parts = probabilityBasedRules.map((rule) => {
      const percentage = i18n.numberToPercentage(
        (rule.probability || 0) * 100,
        { precision: 0 }
      );

      if (rule.target_question_id === null) {
        return i18n.t("questions.endSurveyWithProbability", { percentage });
      }

      const targetOrder = allQuestions.find(
        (q) => q.id === rule.target_question_id
      )?.order;

      return i18n.t("questions.skipToQuestionWithProbability", {
        number: targetOrder,
        percentage,
      });
    });

    return parts.join(i18n.t("questions.probabilityRulesSeparator"));
  };

  return (
    <VStack className="gap-1 pt-2">
      {conditionalRules.map((rule) => {
        const sourceQuestion = allQuestions.find(
          (q) => q.id === rule.source_question_id
        );
        const sourceQuestionText =
          sourceQuestion?.text || i18n.t("questions.untitled");
        const optionText =
          sourceQuestion?.options?.[rule.option_index!] ||
          i18n.t("questions.option", { number: rule.option_index! + 1 });
        return (
          <HStack key={rule.id} className="items-center gap-2">
            <CornerDownRight className="text-primary-600" size={12} />
            <Text className="text-xs text-primary-600">
              {i18n.t("questions.conditionalRuleDisplay", {
                operator: operatorLabels[rule.operator],
                option: optionText,
                sourceQuestion: sourceQuestionText,
                target: getTargetDisplay(rule),
              })}
            </Text>
          </HStack>
        );
      })}
      {probabilityBasedRules.length > 0 && (
        <HStack className="items-center gap-2">
          <CornerDownRight className="text-primary-600" size={12} />
          <Text className="text-xs text-primary-600">
            {getProbabilityRulesDisplay()}
          </Text>
        </HStack>
      )}
      {fallbackDefaultRule && (
        <HStack className="items-center gap-2">
          <CornerDownRight className="text-primary-600" size={12} />
          <Text className="text-xs text-primary-600">
            {getTargetDisplay(fallbackDefaultRule)}
          </Text>
        </HStack>
      )}
    </VStack>
  );
};

export default SkipRules;
